import { AbstractTranscriptEditorCommand } from "./AbstractTranscriptEditorCommand";
import { TranscriptEditorCore } from "../TranscriptEditorCore";
import { TranscriptEditorChangeType } from "../types";

/**
 * Command to adjust the end time of a transcript element
 * 
 * This command changes the end timestamp of a text element in the transcript,
 * which affects audio alignment. It stores the old time for undo operations.
 */
export class AdjustElementEndCommand extends AbstractTranscriptEditorCommand {
  /** ID of the segment containing the element */
  private segmentId: string;
  
  /** ID of the paragraph containing the element */
  private paragraphId: string;
  
  /** ID of the element to adjust */
  private elementId: string;
  
  /** Original end time for undo operations */
  private oldEndTime: number;
  
  /** New end time to set */
  private newEndTime: number;
  
  /**
   * Creates a new AdjustElementEndCommand
   * 
   * @param editor Reference to the editor core
   * @param segmentId ID of the segment containing the element
   * @param paragraphId ID of the paragraph containing the element
   * @param elementId ID of the element to adjust
   * @param oldEndTime Original end time
   * @param newEndTime New end time
   */
  constructor(
    editor: TranscriptEditorCore,
    segmentId: string,
    paragraphId: string,
    elementId: string,
    oldEndTime: number,
    newEndTime: number
  ) {
    super(`Adjust end time to ${newEndTime.toFixed(2)}s`, editor);
    
    this.segmentId = segmentId;
    this.paragraphId = paragraphId;
    this.elementId = elementId;
    this.oldEndTime = oldEndTime;
    this.newEndTime = newEndTime;
    
    // Store metadata about the timing adjustment
    this.setMetadata('elementId', elementId);
    this.setMetadata('oldEndTime', oldEndTime);
    this.setMetadata('newEndTime', newEndTime);
    this.setMetadata('timeChange', newEndTime - oldEndTime);
  }
  
  /**
   * Execute the command
   * 
   * @returns Promise resolving to true if successful
   */
  async execute(): Promise<boolean> {
    try {
      // Get the container
      const container = this.editor.getContainer();
      if (!container) {
        console.error('No container available');
        return false;
      }
      
      // Get the element
      const segment = container.getSegment(this.segmentId);
      if (!segment) {
        console.error(`Segment not found: ${this.segmentId}`);
        return false;
      }
      
      const paragraph = segment.getParagraph(this.paragraphId);
      if (!paragraph) {
        console.error(`Paragraph not found: ${this.paragraphId}`);
        return false;
      }
      
      const element = paragraph.getElement(this.elementId);
      if (!element || element.type !== 'text') {
        console.error(`Text element not found: ${this.elementId}`);
        return false;
      }
      
      // Update the end time
      const textElement = element as any; // Using any to avoid TypeScript errors with property assignment
      textElement.endTime = this.newEndTime;
      
      // Notify listeners about the change
      this.editor.notifyObservers(TranscriptEditorChangeType.ELEMENT_UPDATED, {
        segmentId: this.segmentId,
        paragraphId: this.paragraphId,
        elementId: this.elementId,
        element
      });
      
      return true;
    } catch (error) {
      console.error('Error executing AdjustElementEndCommand:', error);      
      return false;
    }
  }
  
  /**
   * Undo the command
   * 
   * @returns Promise resolving to true if successful
   */
  async undo(): Promise<boolean> {
    
    try {
      // Get the container
      const container = this.editor.getContainer();
      if (!container) {
        console.error('No container available');
        return false;
      }
      
      // Get the element
      const segment = container.getSegment(this.segmentId);
      if (!segment) {
        console.error(`Segment not found: ${this.segmentId}`);
        return false;
      }
      
      const paragraph = segment.getParagraph(this.paragraphId);
      if (!paragraph) {
        console.error(`Paragraph not found: ${this.paragraphId}`);
        return false;
      }
      
      const element = paragraph.getElement(this.elementId);
      if (!element || element.type !== 'text') {
        console.error(`Text element not found: ${this.elementId}`);
        return false;
      }
      
      // Restore the original end time
      const textElement = element as any; // Using any to avoid TypeScript errors with property assignment
      textElement.endTime = this.oldEndTime;
      
      // Notify listeners about the change
      this.editor.notifyObservers(TranscriptEditorChangeType.ELEMENT_UPDATED, {
        segmentId: this.segmentId,
        paragraphId: this.paragraphId,
        elementId: this.elementId,
        element
      });

      return true;
    } catch (error) {
      console.error('Error undoing AdjustElementEndCommand:', error);
      
      return false;
    }
  }
}
  